import { createAction } from '@reduxjs/toolkit'
import { call, put, select, takeEvery } from 'redux-saga/effects'

import { computeFlagBoundsFromItem, computePopupPosition } from '../effects/adjustFlagDetailsPopupPosition'
import { fetchFlagComments } from '../../../api/projects-api'
import { FlagComments } from '../../../models/flags'
import { DEFAULT_DIALOG_HEIGHT } from '../../../shared/constants/sizes'
import managers from '../../lib/managers'
import { Flag, Workspace } from '../../lib/toolBoxes/2D'
import { setLoading } from '../../slices/common'
import {
    selectIsFlagFormOpen,
    setActiveFlag,
    setActiveFlagComments,
    setPendingFlagSwitch,
    showFlagDetails,
} from '../../slices/materialFlags'
import { setDisableFlagTool } from '../../slices/tools'

interface SetupActiveFlagPayload {
    flag_id: number
    openingLinkId?: number
}

export const setupActiveFlagAction = createAction<SetupActiveFlagPayload>('setupActiveFlag')

export function* setupActiveFlagSaga(action: ReturnType<typeof setupActiveFlagAction>) {
    const { flag_id, openingLinkId } = action.payload

    const paperManager = yield call(managers.get2DManager)
    const canvasCenter: paper.Point | undefined = undefined
    const projectView: paper.View | undefined = undefined

    if (paperManager) {
        const flagTool = yield call(paperManager.getTool, Flag.NAME)
        const workspaceTool = yield call(paperManager.getTool, Workspace.NAME)

        const view = workspaceTool.paper.view

        if (view?.element) {
            const allItems = flagTool.getCachedFlags()

            allItems.forEach((item: paper.Item) => {
                if (item.data.onDeselect) {
                    item.data.onDeselect()
                }
            })

            const targetFlag =
                (allItems.find(
                    (item: paper.Item) => item?.data?.flag_id === flag_id && item.data.id === openingLinkId
                ) as paper.Item | undefined) ?? null

            if (targetFlag && targetFlag.data.onSelect) {
                targetFlag.data.onSelect()
            }

            const isFlagFormOpen = yield select(selectIsFlagFormOpen)

            if (isFlagFormOpen) {
                yield put(setPendingFlagSwitch({ flagId: flag_id, paperId: targetFlag?.id }))

                return
            }

            if (targetFlag && openingLinkId) {
                const result = computeFlagBoundsFromItem(targetFlag)

                if (result) {
                    const { flagBounds, projectView } = result
                    const { top, left } = computePopupPosition(flagBounds, projectView.element as HTMLCanvasElement)

                    yield put(
                        showFlagDetails({
                            flagId: flag_id,
                            openingLinkId,
                            position: { x: left, y: top },
                            dialogHeight: DEFAULT_DIALOG_HEIGHT,
                            paperId: targetFlag.id,
                        })
                    )
                }
            }

            yield put(setDisableFlagTool(true))
            yield put(setActiveFlag({ flag_id }))
            yield put(setLoading(true))

            const res: FlagComments[] = yield call(fetchFlagComments, flag_id)

            yield put(setActiveFlagComments({ comments: res }))
            yield put(setLoading(false))

            return
        }
    }

    const payload = {
        flag_id,
        canvasCenter,
        projectView,
        openingLinkId,
    }

    yield put(setActiveFlag(payload))
}

export function* watchForSetupActiveFlag() {
    yield takeEvery(setupActiveFlagAction.type, setupActiveFlagSaga)
}
